import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import { Map, Room } from "@material-ui/icons";
import React from "react";
import { useHistory, useParams } from "react-router";
import { useGetExamSessionQuery } from "../../api/rtk/ExamSessionApi";
import { NewEntityId } from "../../data/constants";
import { ExamDayType } from "../../model/ExamDayType";
import { GetLocalizedName } from "../../model/ExamSessionViewModel";
import { LocationViewModel } from "../../model/LocationViewModel";
import { routesPaths } from "../../Routes";
import DefaultButtons from "../common/buttons/DefaultButtons";
import LinkButton from "../common/buttons/LinkButton";
import TitleWithIcon from "../common/TitleWithIcon";
import ExamDayList from "../examDay/ExamDayList";
import SchedulerOpenButton from "../examsScheduler/SchedulerOpenButton";
import { initialLocation } from "../location/LocationConstants";
import TranslatedMessage from "../translations/TranslatedMessage";
import { navigateBack } from "./../../utils/Navigation";
import ExamSessionLocationFields from "./ExamSessionLocationFields";

const ExamSessionLocationForm: React.FunctionComponent = () => {
  const history = useHistory();
  const { examSessionId, locationId } = useParams<{ examSessionId: string; locationId: string }>();
  const { data: examSession, isFetching: isExamSessionFetching } = useGetExamSessionQuery(examSessionId);

  if (isExamSessionFetching) {
    return <LinearProgress variant="query" />;
  }

  if (examSession === undefined) {
    return <></>;
  }

  const getLocationFromArray = (locationId: string, locations: LocationViewModel[]): LocationViewModel => {
    const location = locations.find((location) => location.id === locationId);
    return location ? location : initialLocation;
  };

  const location = getLocationFromArray(locationId, examSession.locations);

  const locationHasExamType = (examDayType: ExamDayType) =>
    examSession.examDays.filter((d) => d.locationId === locationId && examDayType === d.type).length > 0;

  const handleClose = () => {
    navigateBack(routesPaths.examSession(examSessionId), history, location);
  };

  const handleExamDaySelected = (examDayId: string) => {
    history.push(routesPaths.examDay(examSessionId, location.id, examDayId));
  };

  const handleStartAddingExamDay = () => {
    history.push(routesPaths.examDay(examSessionId, location.id, NewEntityId));
  };

  const handleSchedulingOralExam = () => {
    history.push(routesPaths.employeeOralExamsSchedule(examSessionId, location.id));
  };

  const handleSchedulingWrittenExam = () => {
    history.push(routesPaths.employeeWrittenExamsSchedule(examSessionId, location.id));
  };

  const handleSchedulingCorrectionExam = () => {
    history.push(routesPaths.employeeCorrectionExamsSchedule(examSessionId, location.id));
  };

  const hasOralExams = locationHasExamType(ExamDayType.Oral);
  const hasWrittenExams = locationHasExamType(ExamDayType.Written);
  const hasCorrectionExams = locationHasExamType(ExamDayType.Correction);

  const additionalButtons = (
    <React.Fragment>
      {location.linkToMap && (
        <LinkButton
          tooltip={<TranslatedMessage id="location.field.linkToMap" />}
          href={location.linkToMap}
          target="_blank"
        >
          <Map />
        </LinkButton>
      )}
      {(hasOralExams || hasWrittenExams || hasCorrectionExams) && (
        <SchedulerOpenButton
          onScheduleOralExams={handleSchedulingOralExam}
          scheduleOralExamsAvailable={hasOralExams}
          onScheduleWrittenExams={handleSchedulingWrittenExam}
          scheduleWrittenExamsAvailable={hasWrittenExams}
          onScheduleCorrectionExams={handleSchedulingCorrectionExam}
          scheduleCorrectionExamsAvailable={hasCorrectionExams}
        />
      )}
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <Grid container={true} direction="column" spacing={3}>
        <Grid item={true} container={true} spacing={0} alignItems="baseline">
          <Grid item={true} xs={12} sm={6}>
            <Typography variant="h6" component="span">
              {GetLocalizedName(examSession)}
            </Typography>
            <TitleWithIcon title={location.name} variant="h4">
              <Room />
            </TitleWithIcon>
          </Grid>
          <Grid item={true} xs={12} sm={6}>
            <DefaultButtons
              isDeleteButtonVisible={false}
              isSaveButtonVisible={false}
              onClose={handleClose}
              additionalButtons={additionalButtons}
            />
          </Grid>
        </Grid>

        <Grid item={true} container={true}>
          <ExamSessionLocationFields location={location} />
        </Grid>
        <Grid item={true} container={true}>
          {location.id !== NewEntityId && examSessionId && (
            <ExamDayList
              showAddButton={location.rooms.length > 0}
              isReadOnly={examSession.published}
              examDays={examSession.examDays.filter((examDay) => examDay.locationId === location.id)}
              onExamDaySelected={handleExamDaySelected}
              onStartAddingExamDay={handleStartAddingExamDay}
            />
          )}
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default ExamSessionLocationForm;
